import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, Search, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * B2B — public-records / background-check search for agency users.
 * Subscription-gated (contact careco24.com for price schedule, 50 searches included, 7-day free trial).
 * Backend currently returns MOCKED results — flip to real providers later.
 */
export default function BackgroundRecordsCard() {
    const [sub, setSub] = useState(null);
    const [q, setQ] = useState({ first_name: "", last_name: "", dob: "", state: "", ssn_last4: "" });
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadSub = useCallback(async () => {
        try {
            const r = await api.get("/b2b/background/subscription");
            setSub(r.data);
        } catch (e) { /* noop */ }
    }, []);
    useEffect(() => { loadSub(); }, [loadSub]);

    async function startTrial() {
        try {
            await api.post("/b2b/background/subscription/start");
            toast.success("7-day free trial started — you have 50 searches this month.");
            await loadSub();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to start trial");
        }
    }

    async function search(e) {
        e.preventDefault();
        setBusy(true);
        setResult(null);
        try {
            const r = await api.post("/b2b/background/search", q);
            setResult(r.data);
            await loadSub();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Search failed");
        } finally { setBusy(false); }
    }

    const set = (k, v) => setQ((s) => ({ ...s, [k]: v }));

    if (!sub) return null;

    return (
        <Card className="rounded-2xl border-stone-200" data-testid="b2b-background-card">
            <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <ShieldCheck className="w-5 h-5 text-indigo-700" />
                    <div className="font-outfit font-semibold text-stone-900">Public-records background search</div>
                    <Badge className="bg-indigo-100 text-indigo-800 rounded-full text-[10px] uppercase tracking-wider">B2B add-on</Badge>
                    {sub.active
                        ? <Badge className="bg-emerald-700 text-white rounded-full text-[10px]">{sub.checks_this_month}/{sub.included_this_month} used</Badge>
                        : <Badge className="bg-stone-200 text-stone-800 rounded-full text-[10px]">Not subscribed</Badge>}
                </div>

                <p className="text-sm font-dmsans text-stone-700 mb-3">
                    Sweep public records across NSOPW, OFAC, PACER, state criminal DBs, civil judgments, nurse licensing (NURSYS)
                    and civil driving records in one query. Delivers a summarised risk score in under 30 seconds.
                </p>

                {!sub.active ? (
                    <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-indigo-50/40 p-4 flex items-start gap-3" data-testid="b2b-bg-subscribe-block">
                        <Sparkles className="w-5 h-5 text-indigo-700 mt-0.5" />
                        <div className="flex-1">
                            <div className="font-outfit font-semibold text-stone-900 text-sm">Start a 7-day free trial</div>
                            <p className="text-xs font-dmsans text-stone-700 mt-1">
                                For price schedule, <a href="https://careco24.com/contact" target="_blank" rel="noopener noreferrer" className="underline text-indigo-700 hover:text-indigo-900 font-medium" data-testid="b2b-bg-pricing-contact-link">contact us at careco24.com</a>. 50 searches included during trial · cancel any time.
                            </p>
                            <Button onClick={startTrial} data-testid="b2b-bg-start-trial-btn" className="rounded-full mt-3 h-10 px-5 bg-indigo-700 hover:bg-indigo-800 text-white font-outfit">
                                Start free trial
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <form onSubmit={search} className="grid gap-3 sm:grid-cols-2">
                            <div>
                                <Label>First name *</Label>
                                <Input value={q.first_name} onChange={(e) => set("first_name", e.target.value)} required data-testid="b2b-bg-first-name" className="h-11 rounded-xl mt-1" />
                            </div>
                            <div>
                                <Label>Last name *</Label>
                                <Input value={q.last_name} onChange={(e) => set("last_name", e.target.value)} required data-testid="b2b-bg-last-name" className="h-11 rounded-xl mt-1" />
                            </div>
                            <div>
                                <Label>Date of birth</Label>
                                <Input type="date" value={q.dob} onChange={(e) => set("dob", e.target.value)} data-testid="b2b-bg-dob" className="h-11 rounded-xl mt-1" />
                            </div>
                            <div>
                                <Label>State</Label>
                                <Input maxLength={2} value={q.state} onChange={(e) => set("state", e.target.value.toUpperCase().slice(0, 2))} placeholder="e.g. NY" data-testid="b2b-bg-state" className="h-11 rounded-xl mt-1" />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>SSN — last 4 <span className="text-stone-500 font-normal">(recommended for accuracy)</span></Label>
                                <Input type="password" maxLength={4} value={q.ssn_last4} onChange={(e) => set("ssn_last4", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="xxxx" data-testid="b2b-bg-ssn4" className="h-11 rounded-xl mt-1 font-mono" />
                            </div>
                            <div className="sm:col-span-2">
                                <Button type="submit" disabled={busy} data-testid="b2b-bg-search-btn" className="rounded-full h-11 px-6 bg-indigo-700 hover:bg-indigo-800 text-white font-outfit">
                                    <Search className="w-4 h-4 mr-1" /> {busy ? "Searching…" : "Run background search"}
                                </Button>
                            </div>
                        </form>

                        {result && (
                            <div className="mt-4 rounded-xl border-2 border-stone-200 bg-white p-4" data-testid="b2b-bg-result">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="font-outfit font-semibold text-stone-900 text-sm">
                                        Result for {result.subject.first_name} {result.subject.last_name}
                                    </div>
                                    <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${result.risk_band === "low" ? "bg-emerald-100 text-emerald-800" : result.risk_band === "medium" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                                        Risk: {result.risk_band} ({result.risk_score}/100)
                                    </Badge>
                                    {result.mocked && (
                                        <Badge className="bg-amber-700 text-white rounded-full text-[10px] uppercase tracking-wider">MOCKED</Badge>
                                    )}
                                </div>
                                <p className="text-xs text-stone-700 font-dmsans mb-3">{result.recommendation}</p>
                                <div className="space-y-1.5">
                                    {result.sources_checked.map((s, i) => (
                                        <div key={i} className="flex items-center gap-2 text-xs font-dmsans">
                                            {s.status === "clear" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700" /> :
                                                s.status === "not_checked" ? <AlertTriangle className="w-3.5 h-3.5 text-stone-400" /> :
                                                    <AlertTriangle className="w-3.5 h-3.5 text-amber-700" />}
                                            <span className="text-stone-900 font-medium">{s.name}</span>
                                            <span className="text-stone-500">— {s.status}{s.records_found > 0 ? ` (${s.records_found} record${s.records_found === 1 ? "" : "s"})` : ""}</span>
                                        </div>
                                    ))}
                                </div>
                                {result.disclaimer && (
                                    <p className="text-[10px] text-amber-800 bg-amber-50 rounded px-2 py-1.5 mt-3 font-dmsans">{result.disclaimer}</p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
